import {
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { DsModalService, DsModalVariant } from '@levelaccess/design-system';

import {
  IDclConfig,
  ITableColumn,
  ITableConfig,
  ITableRow,
  SelectAllEvent,
  SortEvent,
} from '../../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { $auditIssueFields } from '../../../../../../../../shared/constants/manual-audit-issues';
import { NgbTableComponent } from '../../../../../../components/table/ngb-table/ngb-table.component';
import { SharedCommonUtility } from '../../../../../../../../shared/utils/common.utility';
import { FINDING_TYPE_LABELS, resolveFindingTypeBucket } from '../constants/finding-type';
import { NgbTableUtilities } from '../../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import {
  DropdownCellComponent,
  IDropdownCellConfig,
  IDropdownItem,
} from '../../../../../../components/table/ngb-table/cells/dropdown-cell/dropdown-cell.component';
import {
  $auditFinding,
  $auditFindingViewport,
  AuditFindingDisabilityAffected,
  AuditFindingQAStatus,
  AuditFindingQAStatusDisplayLabel,
} from '../../../../../../../../shared/constants/audit-finding';
import { Api } from '../../../../../../../../shared/constants/api';
import { LinkedPropertyUtility, ILinkedPropertyData } from '../../../../../../../../shared/utils/linked-property.utility';
import { $auditRule } from '../../../../../../../../shared/constants/audit-rule';
import { $auditStandardSuccessCriteria } from '../../../../../../../../shared/constants/audit-standard-success-criteria';
import { TranslateService } from '../../../../../../translate/translate.service';
import { $auditedDocument } from '../../../../../../../../shared/constants/audited-document';
import { $severity } from '../../../../../../../../shared/constants/accessibility';
import { $uploads } from '../../../../../../../../shared/constants/uploads';
import { RestBuilder } from '../../../../../../services/helpers/rest.builder';
import {
  $auditStandard,
  AuditStandards,
  mapAuditDbStandardToAuditStandard,
} from '../../../../../../../../shared/constants/audit-standard';
import { $sortingOrder } from '../../../../../../../../shared/constants/sort';
import { $paginationParams } from '../../../../../../constants/pagination-params';
import { $task } from '../../../../../../../../shared/constants/task';
import {
  IPopulatedManualFinding,
  IPopulatedManualFindingClient,
  IPopulatedManualFindingSuccessCriteria,
} from '../../../../../../../../shared/interfaces/manual-audit-finding.interface';
import { IUploadWithThumbnailClient } from '../../../../../../../../shared/interfaces/uploads.interface';
import { A11yService } from '../../../../../../services/a11y.service';
import { SharedAuditsUtility } from '../../../../../../../../shared/utils/audits.utility';
import { EvaluationStandards } from '../../../../../../../../shared/constants/manual-evaluation';
import { $navigationCustomState, NavigationCustomState } from '../../../../../../interfaces/navigation-custom-state.interface';
import { IAttachmentData } from '../../../../../../components/table/ngb-table/cells/attachments-cell/attachments-cell.component';
import { ICellConfig } from '../../../../../../components/table/ngb-table/cells/base-cell/base-cell.component';
import { $matchComponent } from '../../../../../../../../shared/constants/match-component';
import { TenantSeveritiesService } from '../../../../../../services/tenant-severities.service';
import { ICustomSeveritiesMap } from '../../../../../../../../shared/interfaces/tenant.interface';
import { SuccessCriteriaService } from '../../../../../../services/success-criteria.service';
import { IAuditStandard } from '../../../../../../../../shared/interfaces/audit-standard.interface';
import { IAuditStandardObjectSuccessCriteria } from '../../../../../../../../shared/interfaces/audit-standard-success-criteria.interface';
import { ISuccessCriteria } from '../../../../../../../../shared/audits/definitions/success-criteria/success-criteria.interface';
import { $successCriteria } from '../../../../../../../../shared/audits/definitions/success-criteria/constants';
import { EapColors } from '../../../../../../shared/eap-colors';
import { Origin } from '../../../../../../../../shared/interfaces/scan.interface';
import { RuleAuditHistoryAction } from '../../../../../../../../shared/constants/rule-audit-history';
import { $manualAuditIssueComment } from '../../../../../../../../shared/constants/comment';
import { $formFields } from '../constants/form-fields';
import { ManualAuditIssueService } from '../../../../../../services/manual-audit-issue.service';
import { CommentService } from '../../../../../../services/comment.service';
import { ModalService } from '../../../../../../services/modal.service';
import { CommonUtility } from '../../../../../../utility/common.utility';
import { IssueActionModalComponent } from '../../../../scans/scan-results/reports/issue-action-modal/issue-action-modal.component';
import { EditManualAuditIssueComponent } from '../view/edit/edit-manual-audit-issue.component';
import {
  ChangeFindingFlagModalComponent,
  IFlagCommentFormValue,
} from '../view/change-finding-flag-modal/change-finding-flag-modal.component';

@Component({
  selector: 'app-manual-eval-findings-table',
  templateUrl: './findings-table.component.html',
  styleUrls: ['./findings-table.component.scss'],
  standalone: false,
})
export class FindingsTableComponent implements OnInit, OnChanges, OnDestroy {
  private restBuilder: RestBuilder = new RestBuilder();
  private subscriptions: Subscription = new Subscription();
  private sortedFindings: IPopulatedManualFindingClient[] = [];
  @Output() private findingsSelectionChange: EventEmitter<void> = new EventEmitter();

  @ViewChild(NgbTableComponent) public findingsTable: NgbTableComponent;
  @Input() public digitalPropertyKey: string = '';
  // ensure selectedColumns is always a Set (never null) — accept Set or array input
  private _selectedColumns: Set<string> = new Set<string>();

  @Input()
  public set selectedColumns(value: Set<string> | string[] | null | undefined) {
    if (value instanceof Set) {
      this._selectedColumns = value;
    } else if (Array.isArray(value)) {
      this._selectedColumns = new Set<string>(value as string[]);
    } else {
      this._selectedColumns = new Set<string>();
    }
  }

  public get selectedColumns(): Set<string> {
    return this._selectedColumns;
  }

  @Input() public findings: IPopulatedManualFindingClient[] = [];
  @Input() public evaluationStandards: EvaluationStandards[] = [];
  @Input() public selectedFindingsMap: Map<string, IPopulatedManualFindingClient>;
  @Input() public page: number = 1;
  @Input() public pageSize: number = 50;
  @Input() public customSeveritiesData: ICustomSeveritiesMap;
  @Input() public showActionsColumn: boolean = false;
  @Input() public userOrigin: Origin | null = null;
  @Input() public editPermission: boolean = false;
  @Input() public dismissalPermission: boolean = false;
  @Input() public manualFindingFlagCreatePermission: boolean = false;
  @Output() public findingActionCompleted: EventEmitter<void> = new EventEmitter();
  @Output() public findingUpdated: EventEmitter<void> = new EventEmitter();

  private customSeverityKeysSortedByRank: $severity[];

  public sortingOrder: Record<string, $sortingOrder>;
  public tableConfig: ITableConfig;
  public tableData: ITableRow[] = [];

  public selectionChanged$: Subject<void>;
  public allPageSelected$: Observable<boolean>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private a11yService: A11yService,
    private tenantSeveritiesService: TenantSeveritiesService,
    private router: Router,
    private successCriteriaService: SuccessCriteriaService,
    private dsModalService: DsModalService,
    private modalService: ModalService,
    private manualAuditIssueService: ManualAuditIssueService,
    private commentService: CommentService,
  ) {
    this.selectionChanged$ = new Subject<void>();
  }

  private get evaluationScanId(): string {
    return this.activatedRoute.snapshot.params[Api.scan_id] ?? '';
  }

  private get linkedPropertyData(): ILinkedPropertyData {
    return LinkedPropertyUtility.fromLinkedPropertyQueryParam(this.activatedRoute.snapshot.queryParams);
  }

  private get workspaceId(): string {
    return this.linkedPropertyData[Api.workspaceId] ?? '';
  }

  private get digitalPropertyId(): string {
    return this.linkedPropertyData[Api.digitalPropertyId] ?? '';
  }

  private getTableConfig(): ITableConfig {
    const config: ITableConfig = {
      columns: {
        checkbox: {
          translationKey: 'select_all_the_findings',
          selectAllEnabled: true,
          hideLabel: true,
          styles: {
            maxWidth: '3rem',
            width: '3rem',
            paddingLeft: '1.5rem',
          },
        },
      },
      caption: this.translateService.instant('label_manual_results'),
      emptyState: {
        iconId: 'magnifying-glass-2',
        title: this.translateService.instant('label_no_search_results'),
        subtitle: this.translateService.instant('label_no_search_results_hint'),
      },
    };

    for (const column of Object.keys(columnDefinitions)) {
      if (this.selectedColumns.has(column)) {
        config.columns[column] = columnDefinitions[column];
      }
    }

    if (this.showActionsColumn) {
      config.columns[ACTIONS_COLUMN_KEY] = actionsColumnDefinition;
    }

    return config;
  }

  private buildSeverityCell(column: $auditIssueFields, finding: IPopulatedManualFindingClient): IDclConfig<ICellConfig> {
    const currentSeverity: $severity = this.getColValue(column, finding);

    return NgbTableUtilities.DSSeverityCell({
      severity: currentSeverity,
      customSeverity: this.customSeveritiesData.get(currentSeverity),
      customSeverityKeysSortedByRank: this.customSeverityKeysSortedByRank,
    });
  }

  private canDismissFinding(finding: IPopulatedManualFindingClient): boolean {
    return finding[$auditFinding.QAStatus] !== AuditFindingQAStatus.dismissed;
  }

  private canIgnoreFinding(finding: IPopulatedManualFindingClient): boolean {
    return finding[$auditFinding.QAStatus] !== AuditFindingQAStatus.ignored;
  }

  private openEditFindingSlideout(finding: IPopulatedManualFindingClient): void {
    const component: EditManualAuditIssueComponent = this.dsModalService.open(
      EditManualAuditIssueComponent,
      DsModalVariant.slideOut,
    ).componentInstance;
    component.evaluationScanId = this.evaluationScanId;
    component.manualAuditIssueId = finding[$auditFinding.issueId];
    component.openedFromActionsDropDown = true;
    component.modalInit();

    this.subscriptions.add(
      component.onSave.subscribe(() => {
        this.findingActionCompleted.emit();
      }),
    );
  }

  private buildActionsCell(finding: IPopulatedManualFindingClient): IDclConfig<IDropdownCellConfig> {
    const dropdownItems: IDropdownItem[] = [];

    if (this.editPermission) {
      dropdownItems.push(this.buildEditDropdownItem(finding));
    }
    if ((this.editPermission || this.dismissalPermission) && this.canDismissFinding(finding)) {
      dropdownItems.push(this.buildDismissDropdownItem(finding));
    }
    if (this.userOrigin === Origin.staff) {
      if (this.editPermission && this.canIgnoreFinding(finding)) {
        dropdownItems.push(this.buildIgnoreDropdownItem(finding));
      }
      if (this.manualFindingFlagCreatePermission) {
        dropdownItems.push(this.buildFlagDropdownItem(finding));
      }
    }

    return NgbTableUtilities.dropdownCell({
      ellipsisIcon: true,
      classes: ['btn-link', 'px-0'],
      dropdownItems,
    });
  }

  private buildEditDropdownItem(finding: IPopulatedManualFindingClient): IDropdownItem {
    return {
      label: this.translateService.instant('action_edit'),
      clickListener: (): void => this.openEditFindingSlideout(finding),
    };
  }

  private openDismissConfirmationModal(): Observable<IssueActionModalComponent> {
    const modalComponentRef: ComponentRef<IssueActionModalComponent> = this.modalService.open(IssueActionModalComponent);
    modalComponentRef.instance.action = RuleAuditHistoryAction.dismiss;
    modalComponentRef.instance.reasonIsRequired = true;

    return modalComponentRef.instance.container.onClose$.pipe(
      take(1),
      filter((): boolean => modalComponentRef.instance.confirmed),
      mergeMap(() => of(modalComponentRef.instance)),
    );
  }

  private buildDismissDropdownItem(finding: IPopulatedManualFindingClient): IDropdownItem {
    return {
      label: this.translateService.instant('label_dismiss_button'),
      clickListener: (): void => {
        this.subscriptions.add(
          this.openDismissConfirmationModal()
            .pipe(
              mergeMap((modal: IssueActionModalComponent) => {
                const reason: string = modal.form.get($formFields.reason).value;
                const formData: FormData = new FormData();
                if (SharedCommonUtility.notNullish(reason)) {
                  formData.append($formFields.reason, reason);
                }
                return this.manualAuditIssueService.dismissQAIssue(
                  formData,
                  this.workspaceId,
                  this.evaluationScanId,
                  this.digitalPropertyId,
                  finding[$auditFinding.issueId],
                );
              }),
            )
            .subscribe(() => {
              this.findingActionCompleted.emit();
              DropdownCellComponent.setFocusOnLastOpened();
            }),
        );
      },
    };
  }

  private buildIgnoreDropdownItem(finding: IPopulatedManualFindingClient): IDropdownItem {
    return {
      label: this.translateService.instant('label_ignore_button'),
      clickListener: (): void => {
        const formData: FormData = new FormData();
        formData.append($formFields.issueStatus, AuditFindingQAStatus.ignored);
        this.subscriptions.add(
          this.manualAuditIssueService
            .updateQAIssueStatus(
              formData,
              this.workspaceId,
              this.evaluationScanId,
              this.digitalPropertyId,
              finding[$auditFinding.issueId],
            )
            .subscribe(() => this.findingActionCompleted.emit()),
        );
      },
    };
  }

  private buildFlagDropdownItem(finding: IPopulatedManualFindingClient): IDropdownItem {
    const isFlagged: boolean = Boolean(finding[$auditFinding.isFlagged]);
    return {
      label: this.translateService.instant(isFlagged ? 'remove_flag' : 'add_flag'),
      clickListener: (): void => {
        const modalRef: ChangeFindingFlagModalComponent = this.dsModalService.open(
          ChangeFindingFlagModalComponent,
          DsModalVariant.center,
        ).componentInstance;
        modalRef.addFlag.set(!isFlagged);
        this.subscriptions.add(
          modalRef.confirmed.subscribe(({ flagReason, message }: IFlagCommentFormValue) => {
            const formData: FormData = CommonUtility.createFormData({
              [$manualAuditIssueComment.manualAuditFindingId]: finding[$auditFinding._id],
              ...(message ? { [$manualAuditIssueComment.message]: message } : {}),
              ...(flagReason ? { [$manualAuditIssueComment.flagReason]: flagReason } : {}),
            });
            this.subscriptions.add(
              this.commentService
                .changeFlagComment(
                  formData,
                  this.workspaceId,
                  this.digitalPropertyId,
                  this.evaluationScanId,
                  finding[$auditFinding.issueId],
                  !isFlagged,
                )
                .subscribe(() => this.findingActionCompleted.emit()),
            );
          }),
        );
      },
    };
  }

  private getCellData(column: $auditIssueFields, finding: IPopulatedManualFindingClient): IDclConfig<ICellConfig> {
    switch (column) {
      case $auditIssueFields.issueId:
        return NgbTableUtilities.linkCell({
          text: this.getColValue(column, finding),
          attributes: {
            routerLink: `./${finding[$auditFinding.issueId]}/${Api.view}`,
            queryParams: this.activatedRoute.snapshot.queryParams,
          },
        });
      case $auditIssueFields.ruleId:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.criteria:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.successCriteria:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.severity:
        return this.buildSeverityCell(column, finding);
      case $auditIssueFields.screen:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.summary:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.conformanceLevelAndVersion:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.evaluationStandard:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.disabilityAffected:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.attachments:
        return NgbTableUtilities.attachmentsCell({
          attachments: this.getColValue(column, finding),
        });
      case $auditIssueFields.issueFrequency:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.instances:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.actualResult:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
          styles: {
            whiteSpace: 'pre-wrap',
          },
        });
      case $auditIssueFields.stepsToReproduce:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
          styles: {
            whiteSpace: 'pre-wrap',
          },
        });
      case $auditIssueFields.recommendation:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
          styles: {
            whiteSpace: 'pre-wrap',
          },
        });
      case $auditIssueFields.url:
        const url: string = this.getColValue(column, finding);
        return NgbTableUtilities.textCell({
          text: url ? url : '',
        });
      case $auditIssueFields.effort:
        return NgbTableUtilities.severityCell({
          severity: this.getColValue(column, finding) as $severity,
        });
      case $auditIssueFields.status:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.createdAt:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.updatedAt:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.task:
        const hasLinkedFinding: boolean = Boolean(finding[$auditFinding.task]);
        if (hasLinkedFinding) {
          return NgbTableUtilities.linkCell({
            icon: {
              iconName: 'link-intact',
              iconWidth: '12px',
              iconHeight: '12px',
              classes: ['internal-link-icon'],
            },
            styles: {
              fontWeight: '500',
            },
            text: this.getColValue(column, finding),
            attributes: {
              routerLink: `../../../${Api.projects}/${finding[$auditFinding.task][$task.projectId]}/${Api.tasks}/${
                finding[$auditFinding.task][$task._id]
              }`,
            },
          });
        }
        return NgbTableUtilities.textCell({
          text: '',
        });
      case $auditIssueFields.category:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      case $auditIssueFields.findingType:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
          styles: { whiteSpace: 'nowrap' },
        });
      case $auditIssueFields.isFlagged:
        return finding.isFlagged
          ? NgbTableUtilities.textWithIconCell({
              text: '',
              icon: {
                iconName: 'flag-2',
                iconWidth: '1rem',
                iconHeight: '1rem',
                iconBeforeText: true,
                iconStyles: {
                  fill: EapColors.RedError,
                },
              },
              iconText: this.translateService.instant('flagged'),
              iconTextStyles: {
                color: EapColors.GreyCopy,
              },
            })
          : NgbTableUtilities.textCell({
              text: '',
            });
      case $auditIssueFields.age:
      case $auditIssueFields.component:
      case $auditIssueFields.xpath:
      case $auditIssueFields.viewport:
      case $auditIssueFields.codeSnippet:
        return NgbTableUtilities.textCell({
          text: this.getColValue(column, finding),
        });
      default:
        return NgbTableUtilities.textCell({
          text: '',
        });
    }
  }

  private getScopedAuditStandards(criteria: IPopulatedManualFindingSuccessCriteria): IAuditStandard[] {
    return SharedAuditsUtility.filterAuditStandardsByEvaluationStandards(
      criteria[$auditStandardSuccessCriteria.auditStandards],
      this.evaluationStandards,
    );
  }

  private getColValue(column: $auditIssueFields, finding: IPopulatedManualFindingClient): any {
    switch (column) {
      case $auditIssueFields.issueId:
        return `${this.digitalPropertyKey}-${finding[$auditFinding.issueId]}`;
      case $auditIssueFields.ruleId:
        return finding[$auditFinding.ruleId]?.[$auditRule.ruleId];
      case $auditIssueFields.criteria:
      case $auditIssueFields.successCriteria: {
        const successCriteria: IAuditStandardObjectSuccessCriteria[] = finding[$auditFinding.ruleId][$auditRule.successCriterias];

        const criteriaList: ISuccessCriteria[] = successCriteria
          .map((criteria: IPopulatedManualFindingSuccessCriteria): ISuccessCriteria | null => {
            const { identifier } = criteria;

            return this.successCriteriaService.getSuccessCriteriaFromStandards(
              identifier,
              this.getScopedAuditStandards(criteria).map(
                (standard: IAuditStandard): AuditStandards =>
                  mapAuditDbStandardToAuditStandard[standard[$auditStandard.standard]],
              ),
            );
          })
          .filter(SharedCommonUtility.notNullish);

        return criteriaList
          .map((criteria: ISuccessCriteria): string => `${criteria[$successCriteria.num]} - ${criteria[$successCriteria.handle]}`)
          .join('\n');
      }
      case $auditIssueFields.severity:
        return finding[$auditRule.severity];
      case $auditIssueFields.screen:
        return finding[$auditFinding.screen];
      case $auditIssueFields.summary:
        return finding[$auditFinding.summary];
      case $auditIssueFields.findingType:
        return this.translateService.instant(FINDING_TYPE_LABELS[resolveFindingTypeBucket(finding)]);
      case $auditIssueFields.isFlagged:
        return finding[$auditFinding.isFlagged] ?? false;
      case $auditIssueFields.evaluationStandard:
      case $auditIssueFields.conformanceLevelAndVersion: {
        const successCriteria: IPopulatedManualFindingSuccessCriteria[] =
          finding[$auditFinding.ruleId]?.[$auditRule.successCriterias] || [];
        const standardGroups: Map<string, Set<string>> = new Map();

        successCriteria.forEach((criteria: IPopulatedManualFindingSuccessCriteria): void => {
          this.getScopedAuditStandards(criteria).forEach((standard: IAuditStandard): void => {
            const key = `${standard[$auditStandard.standard]} ${standard[$auditStandard.version]}`;
            if (!standardGroups.has(key)) {
              standardGroups.set(key, new Set());
            }
            standardGroups.get(key).add(standard[$auditStandard.level]);
          });
        });

        return Array.from(standardGroups.entries())
          .map(([version, levels]: [string, Set<string>]): string => `${version} ${Array.from(levels).sort().join(', ')}`)
          .sort()
          .reverse()
          .join('\n');
      }
      case $auditIssueFields.disabilityAffected:
        let disabilities: string = '';
        if (Array.isArray(finding[$auditFinding.disabilityAffected])) {
          disabilities = finding[$auditFinding.disabilityAffected]
            .map((disability: AuditFindingDisabilityAffected): string => {
              return this.translateService.instant(`issue_field_disability_${disability}`);
            })
            .join(', ');
        } else {
          disabilities = finding[$auditFinding.disabilityAffected] as unknown as string;
        }

        return disabilities;
      case $auditIssueFields.attachments:
        return [
          ...(Array.isArray(finding[$auditFinding.screenshot]) ? finding[$auditFinding.screenshot] : []).map(
            (screenshot: string): IAttachmentData => ({
              iconId: 'attachment-picture',
              href: screenshot,
              isInternal: false,
              isImage: false,
              altText: this.translateService.instant('missing_alt_text'),
            }),
          ),
          ...(
            (Array.isArray(finding[$auditFinding.attachment])
              ? finding[$auditFinding.attachment]
              : []) as IUploadWithThumbnailClient[]
          ).map((file: IUploadWithThumbnailClient): IAttachmentData => {
            const id = file[$uploads._id] as string;
            const token = file[$uploads.downloadToken];
            const href = token
              ? this.restBuilder.create().resources(id, token).getApiUrl()
              : this.restBuilder.create().secureResources(id).getApiUrl();

            const thumbnail = file[$uploads.thumbnail];
            const thumbnailId = thumbnail?.[$uploads._id] as string;
            const thumbnailToken = thumbnail?.[$uploads.downloadToken] as string;
            const isImage: boolean = Boolean(thumbnailId && thumbnailToken);

            return {
              iconId: isImage ? 'attachment-picture' : 'attachment-document',
              href,
              thumbnailSrc: isImage ? this.restBuilder.create().resources(thumbnailId, thumbnailToken).getApiUrl() : undefined,
              isInternal: !token,
              isImage,
              altText: file[$uploads.altText] ?? this.translateService.instant('missing_alt_text'),
            };
          }),
        ];
      case $auditIssueFields.issueFrequency:
        return finding[$auditFinding.issueFrequency]
          ? this.translateService.instant(`issue_field_frequency_${finding[$auditFinding.issueFrequency]}`)
          : '';
      case $auditIssueFields.instances:
        return finding[$auditFinding.instances];
      case $auditIssueFields.actualResult:
        return finding[$auditFinding.actualResult];
      case $auditIssueFields.stepsToReproduce:
        return finding[$auditFinding.stepsToReproduce];
      case $auditIssueFields.recommendation:
        return finding[$auditFinding.recommendation];
      case $auditIssueFields.url:
        return finding[$auditFinding.documentId][$auditedDocument.reference];
      case $auditIssueFields.effort:
        return finding[$auditFinding.functionalImpact];
      case $auditIssueFields.status:
        return this.translateService.instant(AuditFindingQAStatusDisplayLabel[finding[$auditFinding.QAStatus]]);
      case $auditIssueFields.createdAt:
        return new Date(finding[$auditFinding.createdAt]).toLocaleString();
      case $auditIssueFields.updatedAt:
        return new Date(finding[$auditFinding.updatedAt]).toLocaleString();
      case $auditIssueFields.task:
        return finding[$auditFinding.task]?.[$task.taskProjectId] ?? '';
      case $auditIssueFields.category:
        return finding[$auditFinding.ruleId].category;
      case $auditIssueFields.age:
        const daysTranslation: string = this.translateService.instant('days');
        return `${SharedAuditsUtility.getAgeInDays(finding[$auditFinding.createdAt])} ${daysTranslation}`;
      case $auditIssueFields.xpath:
        return finding[$auditFinding.elementReference];
      case $auditIssueFields.codeSnippet:
        return finding[$auditFinding.element];
      case $auditIssueFields.component:
        return finding[$auditFinding.component]?.[$matchComponent.name];
      case $auditIssueFields.viewport:
        return SharedCommonUtility.notNullishOrEmpty(finding[$auditFinding.viewport])
          ? `${finding[$auditFinding.viewport][$auditFindingViewport.width]} x ${
              finding[$auditFinding.viewport][$auditFindingViewport.height]
            }`
          : '';
      default:
        return '';
    }
  }

  private getComparable(column: $auditIssueFields, finding: IPopulatedManualFindingClient): string {
    const value: any = this.getColValue(column, finding);
    if (column === $auditIssueFields.severity || column === $auditIssueFields.effort) {
      return String(comparableSeverity[value]);
    }

    return String(value);
  }

  private getPageFindings(): IPopulatedManualFindingClient[] {
    const startIndex: number = (this.page - 1) * this.pageSize;
    const endIndex: number = this.page * this.pageSize;

    return this.sortedFindings?.slice(startIndex, endIndex) ?? [];
  }

  private getTableData(): ITableRow[] {
    const toTableRow = (finding: IPopulatedManualFindingClient): ITableRow => {
      const row: ITableRow = {
        key: finding[$auditFinding._id],
        data: {
          checkbox: NgbTableUtilities.checkboxCell({
            checked: this.selectedFindingsMap.has(finding[$auditFinding._id]),
            styles: {
              marginTop: '5px',
            },
            attributes: {
              'aria-label': this.translateService.instant('row_select_checkbox'),
            },
            listeners: {
              click: ($event: MouseEvent): void => {
                $event.stopPropagation();
                const id: string = finding[$auditFinding._id];
                if (this.selectedFindingsMap.has(id)) {
                  this.selectedFindingsMap.delete(id);
                } else {
                  this.selectedFindingsMap.set(id, finding);
                }
                this.selectionChanged$.next();
                this.findingsSelectionChange.emit();
              },
            },
          }),
        },
      };

      for (const column of Array.from(this.selectedColumns)) {
        row.data[column] = this.getCellData(column as $auditIssueFields, finding);
      }

      if (this.showActionsColumn) {
        row.data[ACTIONS_COLUMN_KEY] = this.buildActionsCell(finding);
      }

      return row;
    };

    return this.getPageFindings().map(toTableRow);
  }

  private updateSortingQueryParams(column: string, direction: $sortingOrder): void {
    const params: NavigationExtras = {
      relativeTo: this.activatedRoute,
      queryParams: {
        [$paginationParams.sortBy]: column,
        [$paginationParams.direction]: direction,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
      state: {
        [$navigationCustomState.scrollToTop]: false, // don't scroll the page to top after NavigationEnd
      } as NavigationCustomState,
    };

    this.router.navigate([], params).then();
  }

  private getSortingOrderFromQueryParams(): [string, string] {
    const params: Params = this.activatedRoute.snapshot.queryParams;
    return [params[$paginationParams.sortBy], params[$paginationParams.direction]];
  }

  private sortFunction(
    column: $auditIssueFields,
    direction: $sortingOrder,
  ): (a: IPopulatedManualFinding, b: IPopulatedManualFinding) => number {
    return (a: IPopulatedManualFinding, b: IPopulatedManualFinding): number => {
      const aValue: string = this.getComparable(column, a);
      const bValue: string = this.getComparable(column, b);
      return (
        (direction === $sortingOrder.asc
          ? aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
          : bValue.localeCompare(aValue, undefined, { numeric: true, sensitivity: 'base' })) ||
        this.getComparable($auditIssueFields.issueId, a).localeCompare(
          this.getComparable($auditIssueFields.issueId, b),
          undefined,
          { numeric: true, sensitivity: 'base' },
        )
      );
    };
  }

  private sortFindings(column: $auditIssueFields, direction: $sortingOrder): void {
    if (!direction) {
      this.sortedFindings = [...this.findings].sort(this.sortFunction($auditIssueFields.issueId, $sortingOrder.asc));
    } else {
      this.sortedFindings = [...this.findings].sort(this.sortFunction(column, direction));
    }
  }

  private setTableData(): void {
    const [column, direction] = this.getSortingOrderFromQueryParams();
    if (Array.isArray(this.findings)) {
      this.sortFindings(column as $auditIssueFields, direction as $sortingOrder);
    }

    this.tableData = this.getTableData();
    if (this.tableData.length === 0) {
      this.a11yService.setMessage(this.translateService.instant('label_no_search_results'));
    }
  }

  private updateSeverityData(): void {
    this.customSeverityKeysSortedByRank = SharedCommonUtility.notNullishOrEmpty(this.customSeveritiesData)
      ? this.tenantSeveritiesService.getCustomSeverityKeysSortedByRank(this.customSeveritiesData)
      : [];
    this.updateComparableSeverity();
  }

  public updateComparableSeverity(): void {
    if (SharedCommonUtility.notNullishOrEmpty(this.customSeverityKeysSortedByRank)) {
      const customCount: number = this.customSeverityKeysSortedByRank.length;

      comparableSeverity[$severity.high] = comparableSeverity[$severity.high] + customCount;
      comparableSeverity[$severity.critical] = comparableSeverity[$severity.critical] + customCount;

      this.customSeverityKeysSortedByRank.forEach((severity: $severity, index: number) => {
        comparableSeverity[severity] = comparableSeverity[$severity.high] - (index + 1);
      });
    }
  }

  public sortingRequested({ column, direction }: SortEvent): void {
    if (direction === $sortingOrder.all) {
      this.updateSortingQueryParams(null, null);
    } else {
      this.updateSortingQueryParams(column, direction);
    }
  }

  public onSelectAll(event: SelectAllEvent): void {
    this.getPageFindings().forEach((finding: IPopulatedManualFinding) => {
      const id: string = finding[$auditFinding._id];
      if (event.selected) {
        this.selectedFindingsMap.set(id, finding);
      } else {
        this.selectedFindingsMap.delete(id);
      }
    });
    this.setTableData();
    this.selectionChanged$.next();
  }

  public ngOnInit(): void {
    this.updateSeverityData();
    this.subscriptions.add(this.activatedRoute.queryParams.subscribe(this.setTableData.bind(this)));

    this.allPageSelected$ = this.selectionChanged$.pipe(
      switchMap((): Observable<boolean> => {
        this.findingsSelectionChange.emit();
        const findingsIds: string[] = this.getPageFindings().map(
          (finding: IPopulatedManualFinding) => finding[$auditFinding._id],
        );
        const allSelected: boolean =
          findingsIds.length > 0 && findingsIds.every((id: string) => this.selectedFindingsMap.has(id));
        return of(allSelected);
      }),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.tableConfig = this.getTableConfig();

    if (!SharedCommonUtility.isNullish(this.findingsTable)) {
      this.findingsTable.reloadWithNewConfig(this.tableConfig);
    }

    this.updateSeverityData();
    this.setTableData();
    this.selectionChanged$.next();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}

const comparableSeverity: Partial<Record<$severity, number>> = {
  [$severity.info]: 0,
  [$severity.low]: 1,
  [$severity.high]: 2,
  [$severity.critical]: 3,
};

const ACTIONS_COLUMN_KEY: string = 'actions';

const actionsColumnDefinition: ITableColumn = Object.freeze({
  translationKey: 'actions',
  hideLabel: false,
  styles: {
    maxWidth: '6rem',
    width: '6rem',
  },
});

const columnDefinitions: Readonly<Record<string, ITableColumn>> = Object.freeze({
  [$auditIssueFields.issueId]: {
    translationKey: 'finding_id',
    sortingEnabled: true,
    styles: {
      maxWidth: '10%',
      width: '8%',
      minWidth: '8%',
    },
  },
  [$auditIssueFields.ruleId]: {
    translationKey: 'issue_field_ruleId',
    sortingEnabled: true,
  },
  [$auditIssueFields.screen]: {
    translationKey: 'issue_field_screen',
    sortingEnabled: true,
  },
  [$auditIssueFields.summary]: {
    translationKey: 'issue_field_summary',
    sortingEnabled: true,
  },
  [$auditIssueFields.findingType]: {
    translationKey: 'issue_field_findingType',
    sortingEnabled: true,
    styles: {
      whiteSpace: 'nowrap',
    },
  },
  [$auditIssueFields.category]: {
    translationKey: 'issue_field_category',
    sortingEnabled: true,
  },
  [$auditIssueFields.successCriteria]: {
    translationKey: 'issue_field_successCriteria',
    sortingEnabled: true,
    styles: {
      whiteSpace: 'break-spaces',
      minWidth: '10rem',
    },
  },
  [$auditIssueFields.criteria]: {
    translationKey: 'issue_field_criteria',
    sortingEnabled: true,
    styles: {
      whiteSpace: 'break-spaces',
      minWidth: '10rem',
    },
  },
  [$auditIssueFields.severity]: {
    translationKey: 'issue_field_severity',
    sortingEnabled: true,
    styles: {
      whiteSpace: 'nowrap',
    },
  },
  [$auditIssueFields.status]: {
    translationKey: 'issue_field_status',
    sortingEnabled: true,
  },
  [$auditIssueFields.attachments]: {
    translationKey: 'issue_field_attachments',
  },
  [$auditIssueFields.url]: {
    translationKey: 'issue_field_url',
    sortingEnabled: true,
    styles: {
      minWidth: '10rem',
      width: '15%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  [$auditIssueFields.issueFrequency]: {
    translationKey: 'issue_field_issueFrequency',
    sortingEnabled: true,
  },
  [$auditIssueFields.instances]: {
    translationKey: 'issue_field_instances',
    sortingEnabled: true,
  },
  [$auditIssueFields.actualResult]: {
    translationKey: 'issue_field_actualResult',
    sortingEnabled: true,
  },
  [$auditIssueFields.stepsToReproduce]: {
    translationKey: 'issue_field_stepsToReproduce',
    sortingEnabled: true,
  },
  [$auditIssueFields.recommendation]: {
    translationKey: 'issue_field_recommendation',
    sortingEnabled: true,
  },
  [$auditIssueFields.disabilityAffected]: {
    translationKey: 'issue_field_disabilityAffected',
    sortingEnabled: true,
  },
  [$auditIssueFields.conformanceLevelAndVersion]: {
    translationKey: 'issue_field_conformanceLevelAndVersion',
    sortingEnabled: true,
    styles: {
      whiteSpace: 'break-spaces',
      minWidth: '190px',
    },
  },
  [$auditIssueFields.evaluationStandard]: {
    translationKey: 'issue_field_evaluationStandard',
    sortingEnabled: true,
    styles: {
      whiteSpace: 'break-spaces',
      minWidth: '190px',
    },
  },
  [$auditIssueFields.effort]: {
    translationKey: 'issue_field_effort',
    sortingEnabled: true,
  },
  [$auditIssueFields.createdAt]: {
    translationKey: 'issue_field_createdAt',
    sortingEnabled: true,
  },
  [$auditIssueFields.updatedAt]: {
    translationKey: 'issue_field_updatedAt',
    sortingEnabled: true,
  },
  [$auditIssueFields.age]: {
    translationKey: 'issue_field_age',
    sortingEnabled: true,
  },
  [$auditIssueFields.component]: {
    translationKey: 'issue_field_component',
    sortingEnabled: false,
  },
  [$auditIssueFields.xpath]: {
    translationKey: 'issue_field_xpath',
    sortingEnabled: false,
    styles: {
      minWidth: '10rem',
      width: '35%',
      maxWidth: '35%',
      wordBreak: 'break-word',
    },
  },
  [$auditIssueFields.viewport]: {
    translationKey: 'viewport_resolution_px_label',
    sortingEnabled: false,
  },
  [$auditIssueFields.codeSnippet]: {
    translationKey: 'issue_field_codeSnippet',
    sortingEnabled: false,
    styles: {
      minWidth: '10rem',
      width: '35%',
      maxWidth: '35%',
      wordBreak: 'break-word',
    },
  },
  [$auditIssueFields.task]: {
    translationKey: 'task',
    sortingEnabled: true,
  },
  [$auditIssueFields.isFlagged]: {
    translationKey: 'issue_field_isFlagged',
    sortingEnabled: false,
  },
});
